precision highp float;

const float PI = 3.14159265359;
const float WHITE_WAVE_TAIL_LENGTH = 2.0;
const float COLORED_WAVE_NOISE_MULTIPLIER = 1.5;
const float FALLOFF_CONVERSION_FACTOR = 8.0;
const float ANIMATION_TIME_SCALE = 0.6;
const float ANIMATION_SPATIAL_SCALE = 12.0;
const float ANIMATION_SPATIAL_TIME_SCALE = 1.2;
const float ANIMATION_WEIGHT_1 = 0.57;
const float ANIMATION_WEIGHT_2 = 0.43;
const float GRAIN_SCALE = 0.05;
const float WAVE_PRESENCE_COLOR_FACTOR = 0.5;

uniform vec4 uBackground;
uniform vec2 uResolution;
uniform float uTime;
uniform float uWaveAmplitude;
uniform float uWaveSpeed;

uniform float uWaveFrequency;
uniform int uWaveSide;
uniform vec3 uWaveColor1; 
uniform vec3 uWaveColor2;
uniform vec3 uWaveColor3;
uniform vec3 uWaveColor4;
uniform vec3 uWaveColor5;
uniform float uMinFalloff;
uniform float uMaxFalloff;

uniform bool uEchoEnabled;
uniform float uEchoOffset;
uniform float uEchoRadius;
uniform float uEchoFrequency;
uniform float uEchoRingSize;
uniform float uEchoStrokeOpacity;
uniform float uEchoFillOpacity;

varying vec2 vUv;

// =====================================
// UTILITY FUNCTIONS
// =====================================

float tanh(float x) {
    float e2x = exp(2.0 * x);
    return (e2x - 1.0) / (e2x + 1.0);
}

// 3D Perlin noise
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    // First corner
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    // Other corners
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    // Permutations
    i = mod289(i);
    vec4 p = permute(permute(permute(
                i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    // Gradients: 7x7 points over a square, mapped onto an octahedron.
    // The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
    float n_ = 0.142857142857; // 1.0/7.0
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    // Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    // Mix final noise value
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

// =====================================
// WAVE SIDE HELPER FUNCTIONS
// =====================================

float getDistanceFromCenter(vec2 uv, int waveSide) {
    if (waveSide == 0 || waveSide == 1) { 
        // Bottom or Top
        return abs(uv.x - 0.5) * 2.0;
    } else { 
        // Right or Left
        return abs(uv.y - 0.5) * 2.0;
    }
}

float getRawDistanceFromCenter(vec2 uv, int waveSide) {
    if (waveSide == 0 || waveSide == 1) { 
        // Bottom or Top
        return (uv.x - 0.5) * 2.0;
    } else { 
        // Right or Left
        return (uv.y - 0.5) * 2.0;
    }
}

// =====================================
// ANIMATION FUNCTIONS
// =====================================

float calculateAnimatedTailLength(vec2 uv, float time, float minFalloff, float maxFalloff) {
    float tailRange = (maxFalloff - minFalloff) * 0.5;
    float tailCenter = minFalloff + tailRange;
    return tailCenter + sin(time * ANIMATION_TIME_SCALE) * tailRange * ANIMATION_WEIGHT_1 + sin(uv.x * ANIMATION_SPATIAL_SCALE + time * ANIMATION_SPATIAL_TIME_SCALE) * tailRange * ANIMATION_WEIGHT_2;
}

float parabolicInterpolation(float rawDistance, float minVal, float maxVal) {
    float t = (rawDistance + 1.0) * 0.5;
    float t2 = t * t;
    return maxVal - (maxVal - minVal) * (4.0 * t - 4.0 * t2);
}

// =====================================
// WAVE GENERATION AND CALCULATION
// =====================================

float generateWaveNoise(vec2 uv, float time, int waveIndex) {
    float spatialOffset = float(waveIndex) * 0.4 - 0.2;
    float temporalOffset = float(waveIndex) * 1.15;
    float amplitudeMultiplier = 1.0 + float(waveIndex) * 0.15;
    
    vec2 spatialVariation = vec2(spatialOffset, spatialOffset * 0.8);
    
    return snoise(vec3(
        (uv.x + spatialVariation.x) * uWaveFrequency + time * uWaveSpeed * 0.7,
        (uv.y + spatialVariation.y) * uWaveFrequency * 0.5 + time * uWaveSpeed * 0.3,
        time * 0.1 + temporalOffset
    )) * uWaveAmplitude * amplitudeMultiplier;
}

float calculateWaveIntensity(vec2 uv, float noise, float tailLength) {
    float basePosition;
    float baseHeight = uWaveAmplitude;
    float waveTop = baseHeight + noise;
    
    if (uWaveSide == 0) { // Bottom
        basePosition = uv.y;
    } else if (uWaveSide == 1) { // Top
        basePosition = 1.0 - uv.y;
    } else if (uWaveSide == 2) { // Right
        basePosition = 1.0 - uv.x;
    } else { // Left
        basePosition = uv.x;
    }
    
    float falloffDistance = (basePosition - waveTop) * 4.0;
    float falloffRate = FALLOFF_CONVERSION_FACTOR / tailLength;
   
    return max(0.0, 1.0 - tanh(falloffDistance * falloffRate * 0.5));
}

// =====================================
// COLOR FUNCTIONS
// =====================================

vec3 getWaveColor(vec2 uv) {
    float distanceFromCenter;
    vec2 noiseUv;
    
    if (uWaveSide == 0 || uWaveSide == 1) { // Bottom or Top
        distanceFromCenter = uv.x - 0.5;
        // Use horizontal noise orientation for horizontal wave sides
        noiseUv = vec2(uv.x, uv.y);
    } else { // Right or Left
        distanceFromCenter = uv.y - 0.5;
        // Rotate noise orientation for vertical wave sides to maintain consistency
        noiseUv = vec2(uv.y, uv.x);
    }
    
    // Generate noise to create irregular boundaries
    float noiseScale = 12.0; // Controls the frequency of the irregularity
    float noiseAmplitude = 0.005; // Controls the intensity of the irregularity
    float noise = snoise(vec3(noiseUv * noiseScale, uTime * 0.1)) * noiseAmplitude;
    
    // Apply noise to the distance, then take abs to maintain color symmetry
    float irregularDistance = abs(distanceFromCenter + noise);
    float t = irregularDistance * 2.0;
    
    // Simple blur approximation using 5 strategic samples (unrolled)
    vec3 color = vec3(0.0);
    
    // Blended color approach with overlapping contributions
    vec3 finalColor = vec3(0.0);
    float totalWeight = 0.0;
    
    // Color 5 contribution (center)
    float color5Weight = max(0.0, 1.0 - t / 0.2);
    color5Weight = smoothstep(0.0, 1.0, color5Weight);
    finalColor += uWaveColor5 * color5Weight;
    totalWeight += color5Weight;
    
    // Color 4 contribution
    float color4Weight = max(0.0, 1.0 - abs(t - 0.25) / 0.3);
    color4Weight = smoothstep(0.0, 1.0, color4Weight);
    finalColor += uWaveColor4 * color4Weight;
    totalWeight += color4Weight;
    
    // Color 3 contribution
    float color3Weight = max(0.0, 1.0 - abs(t - 0.45) / 0.3);
    color3Weight = smoothstep(0.0, 1.0, color3Weight);
    finalColor += uWaveColor3 * color3Weight;
    totalWeight += color3Weight;
    
    // Color 2 contribution
    float color2Weight = max(0.0, 1.0 - abs(t - 0.675) / 0.35);
    color2Weight = smoothstep(0.0, 1.0, color2Weight);
    finalColor += uWaveColor2 * color2Weight;
    totalWeight += color2Weight;
    
    // Color 1 contribution (outer)
    float color1Weight = max(0.0, (t - 0.6) / 0.4);
    color1Weight = smoothstep(0.0, 1.0, color1Weight);
    finalColor += uWaveColor1 * color1Weight;
    totalWeight += color1Weight;
    
    // Normalize by total weight to avoid oversaturation
    return finalColor / max(totalWeight, 0.001);
}

// =====================================
// ECHO FUNCTIONS
// =====================================

vec2 getEchoCenter(int waveSide, float echoOffset) {
    if (waveSide == 0) {
        // Bottom
        return vec2(0.5, echoOffset);
    } else if (waveSide == 1) {
        // Top
        return vec2(0.5, 1.0 - echoOffset);
    } else if (waveSide == 2) {
        // Right
        return vec2(1.0 - echoOffset, 0.5);
    } else {
        // Left
        return vec2(echoOffset, 0.5);
    }
}

vec3 calculateEchoEffect(vec2 uv, vec2 center, vec2 resolution, float echoRadius, float echoFrequency, float echoRingSize, float echoStrokeOpacity, float echoFillOpacity) {
    float aspect = resolution.x / resolution.y;
    vec2 distVec = (uv - center) * vec2(aspect, 1.0);
    float d = length(distVec);

    float phase = (d - echoRadius) * echoFrequency;
    
    float ringWidthInUV = 0.001 * echoRingSize;
    float ringWidth = ringWidthInUV * echoFrequency;
    float ringPhase = mod(phase, 2.0 * PI);
    
    // Create crisp ring mask
    float ringMask = smoothstep(ringWidth, ringWidth * 0.5, abs(ringPhase - PI));
    
    float angle = atan(distVec.y, distVec.x);
    
    float segmentCount = 1.0 + sin(d * 2.0) * 1.0; // 0-2 segments, varies by distance
    float segmentAngle = angle * segmentCount;
    
    // Add more variation to segment offset between rings
    float segmentOffset = phase * 0.33;
    float rotationSpeed = 0.1 + sin(d * 3.0) * 0.15; // Vary rotation speed by distance (slower)
    float rotatingAngle = segmentAngle + uTime * rotationSpeed + segmentOffset;
    
    float segmentPhase = mod(rotatingAngle, 2.0 * PI) / (2.0 * PI);
    
    float frontEdge = smoothstep(0.98, 1.0, segmentPhase);  // Sharp front edge
    float trailingFade = smoothstep(0.0, 0.8, 1.0 - segmentPhase);  // Long trailing fade
    float segmentMask = max(frontEdge, trailingFade);
    
    float baseIntensity = echoStrokeOpacity * exp(-d * 0.8);
    
    float shimmerTime = uTime * 2.0 + d * 5.0 + angle * 3.0;
    float shimmer = sin(shimmerTime) * 0.3 + 0.7;
    
    // Use wave color at current position with saturation and opacity controls
    vec3 baseColor = getWaveColor(uv);
    
    // Apply saturation boost
    float saturation = 1.2 + shimmer * 0.3;
    vec3 finalColor = baseColor * saturation;
    
    // Calculate ring effect (strokes)
    float ringEffect = ringMask * segmentMask * baseIntensity * shimmer;
    
    // Calculate fill effect (static fills) - smooth version
    float phaseProgress = (PI - phase) / (2.0 * PI);
    
    // Fill from inside out, max out at 3
    float ringsToFill = floor(phaseProgress);
    float fillEffect = min(ringsToFill, 3.0) * echoFillOpacity;
    
    // Combine ring strokes and fills
    float totalEffect = ringEffect + fillEffect;
    
    return finalColor * totalEffect;
}

// =====================================
// MAIN FUNCTION
// =====================================

void main() {
	// Input processing
	vec2 uv = vUv;
	vec2 resolution = uResolution;
	float time = uTime;

	// Wave noise generation
	float whiteWaveNoise = generateWaveNoise(uv, time, 0);
	float coloredWaveNoise = generateWaveNoise(uv, time, 1) * COLORED_WAVE_NOISE_MULTIPLIER;
	
	// Wave intensity calculation
	float whiteWaveIntensity = calculateWaveIntensity(uv, whiteWaveNoise, WHITE_WAVE_TAIL_LENGTH); 
	
	// Colored wave with modulated falloff
	float distanceFromCenter = getDistanceFromCenter(uv, uWaveSide);
	float rawDistanceFromCenter = getRawDistanceFromCenter(uv, uWaveSide);
	float animatedTailLength = calculateAnimatedTailLength(uv, uTime, uMinFalloff, uMaxFalloff);
	float modulatedTailLength = parabolicInterpolation(rawDistanceFromCenter, uMinFalloff, uMaxFalloff) * animatedTailLength / (uMinFalloff + (uMaxFalloff - uMinFalloff) * 0.5);
	float coloredWaveIntensity = calculateWaveIntensity(uv, coloredWaveNoise, modulatedTailLength);

	// Base color composition
	vec4 color = uBackground;
	
	// Grain and wave presence calculation
	float grain = fract(sin(dot(uv * 1000.0, vec2(12.9898, 78.233))) * 43758.5453) * GRAIN_SCALE;
	float wavePresence = max(whiteWaveIntensity, coloredWaveIntensity * WAVE_PRESENCE_COLOR_FACTOR);
	
	// Final color composition
	vec3 positionColor = getWaveColor(uv);
	color.rgb = min(vec3(1.0), color.rgb + vec3(1.0) * whiteWaveIntensity + positionColor * coloredWaveIntensity + vec3(grain) * wavePresence);

	// Echo effect
	if (uEchoEnabled) {
		vec2 center = getEchoCenter(uWaveSide, uEchoOffset);
		
		// Render combined strokes and fills
		vec3 echoEffect = calculateEchoEffect(uv, center, resolution, uEchoRadius, uEchoFrequency, uEchoRingSize, uEchoStrokeOpacity, uEchoFillOpacity);
		
		// Add to final color
		color.rgb = min(vec3(1.0), color.rgb + echoEffect);

		// Ensure color values stay within valid range
		color.rgb = clamp(color.rgb, 0.0, 1.0);
	}

	gl_FragColor = color;
}
